import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Trash2,
  Mail,
  Phone,
  Building2,
  Search,
  Inbox,
  X,
  Download,
  FileText,
  RefreshCcw,
  Briefcase,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { authApi, formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

async function downloadCv(id, filename) {
  try {
    const res = await authApi.get(`/admin/applications/${id}/cv`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "cv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Failed to download CV.");
  }
}

export default function ApplicationsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/applications");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.toLowerCase();
    return items.filter((i) =>
      [i.name, i.email, i.role_applied, i.current_company, i.current_role, i.message, i.phone]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(t))
    );
  }, [items, q]);

  const stats = useMemo(() => {
    const now = new Date();
    const in7d = items.filter((i) => (now - new Date(i.created_at)) / 86400000 <= 7).length;
    const in30d = items.filter((i) => (now - new Date(i.created_at)) / 86400000 <= 30).length;
    return { total: items.length, week: in7d, month: in30d };
  }, [items]);

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await authApi.delete(`/admin/applications/${toDelete.id}`);
      setItems((prev) => prev.filter((i) => i.id !== toDelete.id));
      if (selected?.id === toDelete.id) setSelected(null);
      toast.success("Application deleted.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div data-testid="applications-manager">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {[
          { k: stats.total, v: "Total applications", testid: "app-stat-total" },
          { k: stats.month, v: "Last 30 days", testid: "app-stat-30d" },
          { k: stats.week, v: "Last 7 days", testid: "app-stat-7d" },
        ].map((s) => (
          <div key={s.v} data-testid={s.testid} className="bg-white border border-hairline p-6">
            <div className="font-display text-4xl font-medium text-forest">{s.k}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted2 mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, role, company..."
            data-testid="applications-search"
            className="pl-9 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
          />
        </div>
        <button
          onClick={load}
          data-testid="applications-refresh"
          className="inline-flex items-center gap-2 text-sm text-ink border border-hairline px-4 py-2.5 hover:bg-ink hover:text-bone transition-colors self-start"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-hairline overflow-hidden">
        {loading ? (
          <div data-testid="applications-loading" className="p-12 text-center text-muted2">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="applications-empty" className="p-16 text-center">
            <Inbox className="w-10 h-10 mx-auto text-muted2 mb-4" strokeWidth={1.3} />
            <div className="font-display text-xl text-ink">
              {items.length === 0 ? "No applications yet" : "No results"}
            </div>
            <p className="text-sm text-muted2 mt-2">
              {items.length === 0
                ? "CV submissions from the Careers page will appear here."
                : "No applications match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bone border-b border-hairline">
                <tr className="text-left">
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Candidate</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Role applied</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Current</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Received</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`app-row-${a.id}`}
                    className="border-b border-hairline last:border-b-0 hover:bg-bone/60 cursor-pointer"
                    onClick={() => setSelected(a)}
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-ink">{a.name}</div>
                      <div className="text-xs text-muted2 mt-0.5">{a.email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-block px-2 py-0.5 text-xs bg-forest/8 text-forest border border-forest/15">
                        {a.role_applied}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-ink/80">
                      {a.current_role || a.current_company ? (
                        <div>
                          <div>{a.current_role || "—"}</div>
                          {a.current_company && <div className="text-xs text-muted2 mt-0.5">{a.current_company}</div>}
                        </div>
                      ) : (
                        <span className="text-muted2">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-muted2 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadCv(a.id, a.cv_filename);
                        }}
                        data-testid={`download-${a.id}`}
                        className="text-muted2 hover:text-forest p-2"
                        aria-label="Download CV"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setToDelete(a);
                        }}
                        data-testid={`app-delete-${a.id}`}
                        className="text-muted2 hover:text-red-600 p-2"
                        aria-label="Delete application"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="application-drawer"
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-ink/40" />
          <div
            className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-hairline flex items-center justify-between">
              <div>
                <div className="overline text-terracotta">Application</div>
                <h3 className="font-display text-2xl font-medium text-ink mt-1">{selected.name}</h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="app-drawer-close"
                className="p-2 text-muted2 hover:text-ink"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 text-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted2 mb-1">Role applied</div>
                <span className="inline-block px-2 py-0.5 text-xs bg-forest/8 text-forest border border-forest/15">
                  {selected.role_applied}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted2">Email</div>
                  <a href={`mailto:${selected.email}`} className="text-forest hover:underline">
                    {selected.email}
                  </a>
                </div>
              </div>
              {selected.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted2">Phone</div>
                    <a href={`tel:${selected.phone}`} className="text-forest hover:underline">
                      {selected.phone}
                    </a>
                  </div>
                </div>
              )}
              {(selected.current_role || selected.current_company) && (
                <div className="flex items-start gap-3">
                  <Briefcase className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted2">Current</div>
                    <div className="text-ink">
                      {selected.current_role || "—"}
                      {selected.current_company && (
                        <span className="text-muted2"> · {selected.current_company}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {selected.message && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">Cover message</div>
                  <p className="text-ink whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                </div>
              )}
              <div className="pt-4 border-t border-hairline">
                <div className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">CV</div>
                <div className="flex items-center justify-between border border-hairline p-3 bg-bone/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-forest shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{selected.cv_filename}</div>
                      <div className="text-xs text-muted2">{Math.round((selected.cv_size || 0) / 1024)} KB</div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadCv(selected.id, selected.cv_filename)}
                    data-testid="drawer-download-cv"
                    className="btn-primary text-xs h-9 px-4"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
              </div>
              <div className="text-xs text-muted2 pt-3">Received {fmtDate(selected.created_at)}</div>
            </div>

            <div className="p-6 border-t border-hairline flex gap-3">
              <a
                href={`mailto:${selected.email}?subject=Your application for ${encodeURIComponent(selected.role_applied)} — Novacore Services`}
                data-testid="app-drawer-reply"
                className="btn-primary flex-1 justify-center"
              >
                <Mail className="w-4 h-4" /> Reply
              </a>
              <button
                onClick={() => setToDelete(selected)}
                data-testid="app-drawer-delete"
                className="btn-ghost"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="app-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this application?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the application from{" "}
              <strong>{toDelete?.name}</strong> and delete the attached CV.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="app-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="app-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
